import React, { useEffect, useState, useRef } from 'react';
import { XIcon, CheckCircleIcon, AlertCircleIcon, LoaderIcon } from 'lucide-react';
interface DirectoryApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const DirectoryApplicationModal: React.FC<DirectoryApplicationModalProps> = ({
  isOpen,
  onClose
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscKey);
        document.body.style.overflow = 'auto';
      };
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);
  // Reset form state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setEmail('');
      setWebsite('');
      setSubmitStatus(null);
    }
  }, [isOpen]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !website) {
      setSubmitStatus({
        success: false,
        message: 'Please fill out all fields.'
      });
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const formData = new FormData();
      formData.append('email_address', email);
      formData.append('fields[name]', name);
      formData.append('fields[url]', website);
      formData.append('tags', 'directory-application'); // Add the "directory-application" tag
      const response = await fetch('https://app.kit.com/forms/8424945/subscriptions', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer blo_gIPpz-EVmBjL0RaPcA'
        }
      });
      if (response.ok) {
        setSubmitStatus({
          success: true,
          message: 'Thank you for applying to join our directory! A member of our team will reach out to you shortly.'
        });
        setName('');
        setEmail('');
        setWebsite('');
      } else {
        const errorData = await response.json().catch(() => null);
        if (errorData && errorData.error === 'Member Exists') {
          setSubmitStatus({
            success: true,
            message: 'We already have your application! Our team will be in touch soon.'
          });
        } else {
          throw new Error('Submission failed');
        }
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: 'There was an error submitting your application. Please try again.'
      });
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-179">
      <div ref={modalRef} className="bg-white rounded-xl shadow-lg max-w-md w-full overflow-hidden" data-id="element-180">
        <div className="flex justify-end p-4" data-id="element-181">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-182">
            <XIcon className="w-6 h-6" data-id="element-183" />
          </button>
        </div>
        <div className="px-6 pb-6 pt-2" data-id="element-184">
          <div className="flex justify-center mb-6" data-id="element-185">
            <img src="/Untitled_design_%2886%29.png" alt="Creator Boost Logo" className="h-16" data-id="element-186" />
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2" data-id="element-187">
            Apply to Join Our Directory
          </h2>
          <p className="text-gray-600 text-center mb-6" data-id="element-188">
            Get discovered by new audiences and grow your creator business
          </p>
          <form className="space-y-4" onSubmit={handleSubmit} data-id="element-189">
            <div data-id="element-190">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-191">
                Name
              </label>
              <input id="name" name="fields[name]" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-192" />
            </div>
            <div data-id="element-193">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-194">
                Email
              </label>
              <input id="email" name="email_address" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email address" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-195" />
            </div>
            <div data-id="element-196">
              <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-197">
                Website URL
              </label>
              <input id="website" name="fields[url]" type="text" value={website} onChange={e => setWebsite(e.target.value)} placeholder="example.com" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-198" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-199">
                No need to include https:// or www.
              </p>
            </div>
            {submitStatus && <div className={`p-4 rounded-lg ${submitStatus.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-id="element-200">
                <div className="flex items-start" data-id="element-201">
                  {submitStatus.success ? <CheckCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-green-500" data-id="element-202" /> : <AlertCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-red-500" data-id="element-203" />}
                  <p className="text-sm" data-id="element-204">{submitStatus.message}</p>
                </div>
              </div>}
            <button type="submit" className="w-full px-6 py-3 bg-purple-600 text-white font-medium rounded-lg transition-all hover:shadow-md hover:bg-purple-700 flex items-center justify-center" disabled={isSubmitting} data-id="element-205">
              {isSubmitting ? <>
                  <LoaderIcon className="w-5 h-5 mr-2 animate-spin" data-id="element-206" />
                  <span data-id="element-207">Submitting...</span>
                </> : <span data-id="element-208">Submit Application</span>}
            </button>
            <p className="text-xs text-gray-500 text-center mt-4" data-id="element-209">
              We'll review your application and get back to you soon.
            </p>
          </form>
        </div>
      </div>
    </div>;
};